import {
  ApplicationAction,
  ApplicationActionTitle,
} from '@features/applications';
import { applicationActions } from '@features/applications/components/BulkActions/constants';
import {
  getCTAButtonColor,
  getSendWithMessageButtonText,
  getSendWithoutMessageButtonText,
  getTitle,
} from '@features/applications/components/BulkActions/utils';
import HireflixLowCreditModal from '@features/applications/components/HireflixLowCreditModal/HireflixLowCreditModal';
import HireflixPositionNotFoundModal from '@features/applications/components/HireflixPositionNotFoundModal/HireflixPositionNotFoundModal';
import NotEnoughSlotsModal from '@features/applications/components/NotEnoughSlotsModal/NotEnoughSlotsModal';
import SingleMessageModal from '@features/applications/components/SingleMessageModal/SingleMessageModal';
import { useBulkAction } from '@features/applications/hooks/useBulkAction';
import { useCheckAvailableCalendarSlots } from '@features/applications/hooks/useCheckAvailableCalendarSlots';
import { useCheckEnterpriseAvailableCalendarSlots } from '@features/applications/hooks/useCheckEnterpriseAvailableCalendarSlots';
import { useEnterpriseBulkAction } from '@features/applications/hooks/useEnterpriseBulkAction';
import { useHireflixInviteToInterview } from '@features/applications/hooks/useHireflixInviteToInterview';
import BulkTeamActionModal from '@features/employerTeam/components/BulkTeamActionModal';
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Tooltip,
} from '@mui/material';
import { authContext } from '@staffedupusa/shared-components';
import { FC, useContext, useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import MarkInterviewModal from '../MarkInterviewModal/MarkInterviewModal';

type BulkActionsProps = {
  disabled: boolean;
  selectedApplicationsIds: number[];
  handlePageNo?: () => void;
  filterData: boolean;
  selectedCompanyId?: string | number;
  selectedStatus?: string;
};

const BulkActions: FC<BulkActionsProps> = ({
  disabled,
  handlePageNo,
  filterData,
  selectedCompanyId,
  selectedStatus,
  selectedApplicationsIds,
}) => {
  const { authData } = useContext(authContext);
  const [isBulkActionModalOpen, setIsBulkActionModalOpen] =
    useState<boolean>(false);
  const [isDateRangePickerShown, setIsDateRangePickerShown] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [isNotEnoughSlotsModalOpen, setIsNotEnoughSlotsModalOpen] =
    useState<boolean>(false);
  const [startTimestamp, setStartTimestamp] = useState<number | null>(null);
  const [endTimestamp, setEndTimestamp] = useState<number | null>(null);
  const [formattedDate, setFormattedDate] = useState<string>('');
  const [selectedAction, setSelectedAction] =
    useState<ApplicationAction | null>(null);
  const [showBulkActionTeamModal, setShowBulkActionTeamModal] =
    useState<string>('');
  const [interviewActionModal, setInterviewActionModal] = useState<
    'completed' | 'missed' | ''
  >('');
  const [errorText, setErrorText] = useState<string>('');
  const [progress, setProgress] = useState(0);

  const { calendarSlotsData } = useCheckAvailableCalendarSlots({
    enabled: !!(authData?.user?.company && selectedApplicationsIds?.length),
    application_ids: selectedApplicationsIds,
  });
  const { enterpriseCalendarSlotsData } =
    useCheckEnterpriseAvailableCalendarSlots({
      enabled: !!(
        authData?.user?.enterprise &&
        selectedCompanyId &&
        selectedApplicationsIds?.length
      ),
      application_ids: selectedApplicationsIds,
    });

  const {
    bulkActionData,
    createBulkAction,
    isCreateBulkActionLoading,
    isCreateBulkActionSuccess,
    resetCreateBulkAction,
  } = useBulkAction({
    setErrorText,
  });

  const {
    enterpriseBulkActionData,
    enterpriseCreateBulkAction,
    isEnterpriseCreateBulkActionLoading,
    isEnterpriseCreateBulkActionSuccess,
    resetEnterpriseCreateBulkAction,
  } = useEnterpriseBulkAction({
    setErrorText,
  });

  const { hireflixInviteToInterview, isHireflixInviteToInterviewLoading } =
    useHireflixInviteToInterview({ setErrorText });

  const hireflixPlan = authData?.user?.company?.hire_flix_plan;
  const hireflixPlanStatus = hireflixPlan?.status || 'Trial';

  const [hireflixLowCreditModal, setHireflixLowCreditModal] =
    useState<boolean>(false);
  const [pendingHireflixPayload, setPendingHireflixPayload] = useState<
    any | null
  >(null);

  const [hireflixPositionNotFoundModal, setHireflixPositionNotFoundModal] =
    useState<boolean>(false);
  const [hireflixPosition, setHireflixPosition] = useState<string | undefined>(
    undefined,
  );

  const getSuccessMessage = (isMessageRequired: Boolean) => {
    let successMessage = 'Messaged applications';

    const numberOfMessages = selectedApplicationsIds?.length;
    const action = selectedAction ? selectedAction.actionType : '';

    const getPluralizedWord = (
      count: number,
      singular: string,
      plural: string,
    ) => {
      return count === 1 ? singular : plural;
    };

    successMessage =
      action === 'invite to interview block'
        ? `Sent ${numberOfMessages} interview ${getPluralizedWord(numberOfMessages, 'invite', 'invites')}`
        : action === 'invite to hireflix interview'
          ? `Sent ${numberOfMessages} Hireflix Interview ${getPluralizedWord(numberOfMessages, 'invite', 'invites')}`
          : action === 'hire' && isMessageRequired
            ? `Sent ${numberOfMessages} job ${getPluralizedWord(numberOfMessages, 'offer', 'offers')}`
            : action === 'hire' && !isMessageRequired
              ? `Moved ${numberOfMessages} to hired`
              : action === 'save'
                ? `Saved ${numberOfMessages} ${getPluralizedWord(numberOfMessages, 'application', 'applications')}`
                : action === 'decline'
                  ? `Declined ${numberOfMessages} ${getPluralizedWord(numberOfMessages, 'application', 'applications')}`
                  : action === 'archive'
                    ? `Archived ${numberOfMessages} ${getPluralizedWord(numberOfMessages, 'application', 'applications')}`
                    : action === 'Scheduled Interview'
                      ? `Sent ${numberOfMessages} interview ${getPluralizedWord(numberOfMessages, 'invite', 'invites')}`
                      : `Messaged ${numberOfMessages} ${getPluralizedWord(numberOfMessages, 'application', 'applications')}`;

    return numberOfMessages ? successMessage : successMessage;
  };

  const handleSend = async (isMessageRequired: boolean, message: string) => {
    const batchSize = 10;
    const ids = selectedApplicationsIds.slice();
    const totalBatches = Math.ceil(ids.length / batchSize);

    setProgress(0);

    const basePayload = {
      action: selectedAction?.actionType as string,
      ...(isMessageRequired && { message }),
      ...(selectedAction?.actionType === 'Invite to Open Interview' && {
        slot_interview: false,
      }),
      ...(selectedAction?.actionType === 'invite to interview block' && {
        slot_interview: true,
      }),
      ...(isDateRangePickerShown &&
        selectedAction?.actionType === 'Scheduled Interview' && {
          start_time: startTimestamp,
          end_time: endTimestamp,
          eventDate: formattedDate,
        }),
    };

    // For company: no batching
    if (authData.user?.company) {
      if (selectedAction?.actionType === 'invite to hireflix interview') {
        // Prepare payload
        const payload = {
          application_ids: selectedApplicationsIds,
          body: message,
        };

        // Check low credit (paid plan && credits <= 5 && auto_refill disabled)
        const creditsLeft = hireflixPlan?.remaining_credit_limit ?? 0;
        const autoRefill = hireflixPlan?.auto_refill;

        if (hireflixPlanStatus === 'Paid' && creditsLeft <= 5 && !autoRefill) {
          setPendingHireflixPayload(payload);
          setHireflixLowCreditModal(true);
        } else {
          // Send immediately
          await hireflixInviteToInterview(payload, {
            onSuccess: (data: any) => {
              if (data && data?.status === 200) {
                handlePageNo && handlePageNo();
                setIsBulkActionModalOpen(false);
                setIsDateRangePickerShown(false);
                setSelectedAction(null);
                setStartTimestamp(null);
                setEndTimestamp(null);
                setFormattedDate('');
                toast.success(
                  data?.message || getSuccessMessage(isMessageRequired),
                );
                setSuccessMessage(getSuccessMessage(isMessageRequired));
              } else {
                // If Hireflix reports missing position, show position not found modal
                if (
                  [
                    'No position found on HireFlix please create one before continuing.',
                    'Looks like the HireFlix invite hasn’t been created or already exist.',
                  ].includes(data?.message)
                ) {
                  setHireflixPositionNotFoundModal(true);
                }
                setErrorText(
                  data?.message ||
                    'Unable to send Hireflix invites. Please try again.',
                );
              }
            },
          });
        }
      } else {
        const bulkActionPayload = {
          ...basePayload,
          ids: ids.join(','),
        };

        await createBulkAction(bulkActionPayload, {
          onSuccess: (data) => {
            if (data?.status === 200) {
              handlePageNo && handlePageNo();
              setIsBulkActionModalOpen(false);
              setIsDateRangePickerShown(false);

              setSelectedAction(null);
              setStartTimestamp(null);
              setEndTimestamp(null);
              setFormattedDate('');
              toast.success(getSuccessMessage(isMessageRequired));
              setSuccessMessage(getSuccessMessage(isMessageRequired));
            }
          },
        });
      }

      setProgress(100);
      return; // Early return: done for company
    }

    // For enterprise: batch processing
    if (authData.user?.enterprise) {
      for (let batchIndex = 0; batchIndex < totalBatches; batchIndex++) {
        const batchIds = ids.splice(0, batchSize);
        const bulkActionPayload = {
          ...basePayload,
          ids: batchIds.join(','),
        };

        await enterpriseCreateBulkAction(bulkActionPayload, {
          onSuccess: (data) => {
            if (data?.status == 200) {
              handlePageNo && handlePageNo();
              setProgress(((batchIndex + 1) / totalBatches) * 100);
              toast.success(getSuccessMessage(isMessageRequired));
              setSuccessMessage(getSuccessMessage(isMessageRequired));
            }
          },
        });
      }

      setIsBulkActionModalOpen(false);
      setIsDateRangePickerShown(false);
      setSelectedAction(null);
      setStartTimestamp(null);
      setEndTimestamp(null);
      setFormattedDate('');
      setProgress(100);
    }
  };

  const handleBulkActionSelect = (actionTitle: ApplicationActionTitle) => {
    if (
      actionTitle === 'Mark as Terminated' ||
      actionTitle === 'Mark as Resigned'
    ) {
      setShowBulkActionTeamModal(
        actionTitle === 'Mark as Resigned' ? 'resign' : 'terminate',
      );
      return;
    }
    if (
      actionTitle === 'Mark as Interview Completed' ||
      actionTitle === 'Mark as Interview Missed'
    ) {
      setInterviewActionModal(
        actionTitle === 'Mark as Interview Completed' ? 'completed' : 'missed',
      );
      return;
    }
    if (actionTitle === 'Interview Invitation') {
      setIsDateRangePickerShown(true);
    }
    setSelectedAction(
      applicationActions.find((action) => action.title === actionTitle)!,
    );
    if (
      actionTitle !== 'Invite to Interview Block' &&
      actionTitle !== 'Invite to Open Interview'
    ) {
      return setIsBulkActionModalOpen(true);
    }
    if (authData?.user?.company) {
      if (
        actionTitle === 'Invite to Interview Block' &&
        calendarSlotsData?.status === 200 &&
        calendarSlotsData?.has_slots_available
      ) {
        return setIsBulkActionModalOpen(true);
      }
      if (
        actionTitle === 'Invite to Open Interview' &&
        calendarSlotsData?.status === 200 &&
        calendarSlotsData?.has_blocks
      ) {
        return setIsBulkActionModalOpen(true);
      }
      setIsNotEnoughSlotsModalOpen(true);
    } else if (authData?.user?.enterprise) {
      if (
        actionTitle === 'Invite to Interview Block' &&
        enterpriseCalendarSlotsData?.status === 200 &&
        enterpriseCalendarSlotsData?.has_slots_available
      ) {
        return setIsBulkActionModalOpen(true);
      }
      if (
        actionTitle === 'Invite to Open Interview' &&
        enterpriseCalendarSlotsData?.status === 200 &&
        enterpriseCalendarSlotsData?.has_blocks
      ) {
        return setIsBulkActionModalOpen(true);
      }
      setIsNotEnoughSlotsModalOpen(true);
    }
  };

  useEffect(() => {
    if (isCreateBulkActionSuccess) {
      setTimeout(() => {
        setIsBulkActionModalOpen(false);
        resetCreateBulkAction();
      }, 2000);
    }
    if (isEnterpriseCreateBulkActionSuccess) {
      setTimeout(() => {
        setIsBulkActionModalOpen(false);
        resetEnterpriseCreateBulkAction();
      }, 2000);
    }
  }, [isCreateBulkActionSuccess, isEnterpriseCreateBulkActionSuccess]);
  return (
    <>
      <Tooltip
        title={
          disabled ||
          (authData?.user?.enterprise !== null && !selectedCompanyId)
            ? authData?.user?.enterprise !== null
              ? `Please select at least 1 company and application`
              : `Please select at least 1 application`
            : ''
        }
        disableHoverListener={
          !disabled &&
          authData?.user?.enterprise === null &&
          selectedCompanyId !== null
        }
        arrow
      >
        <span>
          <FormControl
            size="small"
            variant="outlined"
            disabled={
              disabled ||
              (authData?.user?.enterprise !== null && !selectedCompanyId)
            }
            fullWidth
          >
            <InputLabel
              id="bulk-actions-input"
              style={{ fontSize: '14px', lineHeight: '14px' }}
            >
              Bulk Actions
            </InputLabel>
            <Select
              size="small"
              label="Bulk Actions"
              labelId="bulk-actions-input"
              value=""
              onChange={(event) =>
                handleBulkActionSelect(
                  event.target.value as ApplicationActionTitle,
                )
              }
              sx={{ fontSize: '14px', borderRadius: 5, height: '32px' }}
              MenuProps={{
                PaperProps: {
                  style: {
                    borderRadius: 5,
                  },
                },
              }}
            >
              {applicationActions
                .filter((item) => {
                  if (item.actionType === 'Scheduled Interview') return false;

                  if (selectedStatus === 'hired') {
                    return ['message', 'resined', 'terminated'].includes(
                      item.actionType,
                    );
                  }
                  if (selectedStatus === 'interview process') {
                    return (
                      item.actionType !== 'resined' &&
                      item.actionType !== 'terminated'
                    );
                  }
                  return (
                    item.actionType !== 'resined' &&
                    item.actionType !== 'terminated' &&
                    item.actionType !== 'Interview Missed' &&
                    item.actionType !== 'Interview Completed'
                  );
                })
                .filter((item) =>
                  filterData ? item.actionType === 'message' : true,
                )
                .map((action) => (
                  <MenuItem key={action.title} value={action.title}>
                    {action.title}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        </span>
      </Tooltip>
      {isBulkActionModalOpen && (
        <SingleMessageModal
          isOpen={isBulkActionModalOpen}
          selectedCompanyId={selectedCompanyId}
          isDateRangePickerShown={isDateRangePickerShown}
          handleClose={() => {
            setIsDateRangePickerShown(false);
            setIsBulkActionModalOpen(false);
            setStartTimestamp(null);
            setEndTimestamp(null);
            setFormattedDate('');
          }}
          action={selectedAction as ApplicationAction}
          title={getTitle(selectedAction as ApplicationAction)}
          subtitle={`${selectedApplicationsIds.length} ${
            selectedApplicationsIds.length === 1
              ? 'application'
              : 'applications'
          } selected`}
          isSubtitleShown={
            (!isCreateBulkActionSuccess ||
              !isEnterpriseCreateBulkActionSuccess) &&
            selectedApplicationsIds?.length > 0
          }
          submitWithMessageText={getSendWithMessageButtonText(
            selectedAction as ApplicationAction,
          )}
          submitWithoutMessageText={getSendWithoutMessageButtonText(
            selectedAction as ApplicationAction,
          )}
          errorText={errorText}
          setErrorText={setErrorText}
          showProgress={
            isCreateBulkActionLoading ||
            isEnterpriseCreateBulkActionLoading ||
            isEnterpriseCreateBulkActionSuccess ||
            isCreateBulkActionSuccess ||
            isHireflixInviteToInterviewLoading
          }
          isSuccessMessageShown={
            authData.user?.company
              ? isCreateBulkActionSuccess
              : isEnterpriseCreateBulkActionSuccess
          }
          successMessage={successMessage}
          showSendWithoutMessage={selectedAction?.showSendWithoutMessage}
          progress={progress}
          buttonColor={getCTAButtonColor(selectedAction?.title)}
          handleSubmit={handleSend}
          disabled={
            isDateRangePickerShown && (!startTimestamp || !endTimestamp)
          }
          selectedApplicationsIds={selectedApplicationsIds}
          setStartTimestamp={setStartTimestamp}
          setEndTimestamp={setEndTimestamp}
          setFormattedDate={setFormattedDate}
          handlePageNo={handlePageNo}
        />
      )}
      {Boolean(showBulkActionTeamModal) && (
        <BulkTeamActionModal
          isOpen={Boolean(showBulkActionTeamModal)}
          handleClose={() => {
            setShowBulkActionTeamModal('');
          }}
          selectedApplicationIds={selectedApplicationsIds}
          action={showBulkActionTeamModal as 'terminate' | 'resign' | 'team'}
          handleReset={handlePageNo}
        />
      )}
      <MarkInterviewModal
        handleClose={() => setInterviewActionModal('')}
        isOpen={Boolean(interviewActionModal)}
        type={interviewActionModal}
        selectedApplicationsIds={selectedApplicationsIds}
      />
      {/* Hireflix low credit confirmation modal (for bulk invites) */}
      {hireflixLowCreditModal && (
        <HireflixLowCreditModal
          isOpen={hireflixLowCreditModal}
          handleClose={() => {
            setHireflixLowCreditModal(false);
            setPendingHireflixPayload(null);
          }}
          invitesCount={selectedApplicationsIds.length}
          creditsLeft={hireflixPlan?.remaining_credit_limit ?? 0}
          onConfirmSend={() => {
            if (pendingHireflixPayload) {
              hireflixInviteToInterview(pendingHireflixPayload, {
                onSuccess: (data: any) => {
                  if (data && data?.status === 200) {
                    handlePageNo && handlePageNo();
                    setIsBulkActionModalOpen(false);
                    setIsDateRangePickerShown(false);
                    setSelectedAction(null);
                    setStartTimestamp(null);
                    setEndTimestamp(null);
                    setFormattedDate('');
                    toast.success(data?.message || getSuccessMessage(false));
                    setSuccessMessage(getSuccessMessage(false));
                  } else {
                    if (
                      data?.message ===
                      'No position found on HireFlix please create one before continuing.'
                    ) {
                      setHireflixPositionNotFoundModal(true);
                    }
                    setErrorText(
                      data?.message ||
                        'Unable to send Hireflix invites. Please try again.',
                    );
                  }
                },
              });
            }
            setHireflixLowCreditModal(false);
            setPendingHireflixPayload(null);
          }}
        />
      )}

      {/* Hireflix position not found modal */}
      {hireflixPositionNotFoundModal && (
        <HireflixPositionNotFoundModal
          position={hireflixPosition}
          isOpen={hireflixPositionNotFoundModal}
          handleClose={() => setHireflixPositionNotFoundModal(false)}
        />
      )}
      <NotEnoughSlotsModal
        isOpen={isNotEnoughSlotsModalOpen}
        handleClose={() => setIsNotEnoughSlotsModalOpen(false)}
      />
    </>
  );
};

export default BulkActions;
